/** Typed error codes, mirroring the backend's src/core/errors/codes.ts. */
export type ApiErrorCode =
  | 'AUTH_REQUIRED'
  | 'AUTH_TOKEN_INVALID'
  | 'AUTH_TOKEN_EXPIRED'
  | 'AUTH_TOKEN_REVOKED'
  | 'AUTH_DOMAIN_NOT_ALLOWED'
  | 'ORG_NOT_FOUND'
  | 'NOT_A_MEMBER'
  | 'MEMBER_DISABLED'
  | 'ACCESS_DENIED'
  | 'VALIDATION_FAILED'
  | 'NOT_FOUND'
  | 'CONFLICT_VERSION'
  | 'RECENT_STATUS_CHANGE'
  | 'RECENT_DUPLICATE_ACTION'
  | 'CURSOR_INVALID'
  | 'FILTER_TOO_BROAD'
  | 'FILTER_UNSUPPORTED'
  | 'BOARD_TOO_LARGE'
  | 'LAST_OWNER_PROTECTED'
  | 'TIMEOUT'
  | 'NETWORK'
  | 'INTERNAL'
  | (string & {})

export class ApiRequestError extends Error {
  readonly code?: ApiErrorCode
  readonly status?: number
  readonly details?: unknown
  readonly requestId?: string

  constructor(
    message: string,
    code?: ApiErrorCode,
    status?: number,
    details?: unknown,
    requestId?: string,
  ) {
    super(message)
    this.name = 'ApiRequestError'
    this.code = code
    this.status = status
    this.details = details
    this.requestId = requestId
  }
}

export function isApiError(e: unknown): e is ApiRequestError {
  return e instanceof ApiRequestError
}

export function getErrorMessage(e: unknown): string {
  if (isApiError(e) && e.code === 'CONFLICT_VERSION') {
    return 'Someone changed this while you were editing. Refresh the latest version and try your change again.'
  }
  if (isApiError(e)) return e.message
  if (e instanceof Error) return e.message
  return 'Something went wrong. Please try again.'
}

/** 4xx errors are the caller's fault — retrying will not help. */
export function isClientError(e: unknown): boolean {
  return isApiError(e) && typeof e.status === 'number' && e.status >= 400 && e.status < 500
}

/** A conflict should explain the collaboration problem, not look like a random server error. */
export function getErrorTitle(e: unknown): string {
  if (isApiError(e) && e.code === 'CONFLICT_VERSION') return 'Someone changed this while you were editing'
  return 'Something went wrong'
}

export function getRequestId(e: unknown): string | undefined {
  return isApiError(e) ? e.requestId : undefined
}

export function errorDiagnostics(e: unknown): string {
  if (!isApiError(e)) return getErrorMessage(e)
  return [
    e.message,
    e.code ? `Code: ${e.code}` : null,
    e.status ? `HTTP: ${e.status}` : null,
    e.requestId ? `Request ID: ${e.requestId}` : null,
  ].filter(Boolean).join('\n')
}
